import { makeLmsRequest } from '../service/LmsClient';
import { External } from '../../external';
import { AppCache } from '../../stores';
import { get } from 'svelte/store';
import { ignore, makeWebsiteRequest } from '../../Common/tools';
import {
  applyLocalizedCircleDetails,
  applyLocalizedFields,
  buildLocalizedPayload,
  CIRCLE_LOCALIZED_FIELDS,
  GROUP_LOCALIZED_FIELDS,
  PAGE_LOCALIZED_FIELDS
} from './LmsLocaleUtils';

export let LmsUtils = {
  getStartEndTime: (activePeriod) => {
    let fromTime = activePeriod?.from || new Date(External.createdTime * 1000);
    let toTime = activePeriod?.to || new Date();

    // we are NOT going to use date.toISOString().slice(0, 10) here. It will give us the date in a proper ISO format but in UTC!
    // And stats are in the website timezone. When a customer selects a date, we'll send the selected date like '2024-07-23' (as seen in their timezone)
    // and the server will use it as if it's in the website timezone.
    let from = External.Tools.dateToISOStr(fromTime);
    let to = External.Tools.dateToISOStr(toTime);
    return {
      from, to, fromTime, toTime
    };
  },
  getByPageUrl: function(circleDetails, pageUrl) {
    let arr = circleDetails.pages.filter(p => p.id === pageUrl);
    if (arr.length === 0) {
      for (let idx in circleDetails.innerCircles) {
        let innerCircle = circleDetails.innerCircles[idx];
        let arr1 = innerCircle.pages.filter(p => p.pageUniqueUrl === pageUrl);
        if (arr1.length > 0) {
          return arr1[0];
        }
      }
      return null;
    } else {
      return arr[0];
    }
  },
  getCircleAndPageUrl: function() {
    const DELIMITER = 'circles/';
    const url = window.location.href;
    let path = url.substring(url.indexOf(DELIMITER) + DELIMITER.length);
    let parts = path.split('/');
    let circleUrl = parts[0];
    let pageUrl
    if (parts.length > 1) {

    }

    if (afterCircles.includes('/')) {
      let arr = afterCircles.split('/');
      return {
        circleUrl: arr[0],
        pageUrl: arr[1]
      };
    } else {
      return {
        circleUrl: afterCircles,
        pageUrl: null
      };
    }
  },
  removeCachedCircle: (id) => {
    let cache = get(AppCache);
    if (!cache.lmsCircles) {
      return;
    }
    let circles = cache.lmsCircles.filter(circle => circle.id !== id);
    if (circles.length !== cache.lmsCircles.length) {
      AppCache.update(cache => {
        cache.lmsCircles = circles;
        return cache;
      });
    }
  },
  updateCachedCircle: (circle) => {
    let cache = get(AppCache);
    if (!cache.lmsCircles) {
      return;
    }
    let propsToUpdate = ['name', 'url', 'published'];
    let changed = false;
    let circles = cache.lmsCircles;
    circles.filter(c => c.id === circle.id).forEach(c => {
      propsToUpdate.forEach(prop => {
        if (!External.isUndefined(circle[prop])) {
          c[prop] = circle[prop];
          changed = true;
        }
      });
    });

    if (changed) {
      AppCache.update(cache => {
        cache.lmsCircles = circles;
        return cache;
      });
    }
  },
  clearCachedCircles: () => {
    AppCache.update(cache => {
      cache.lmsCircles = null;
      return cache;
    });
  },
  listCircles: async () => {
    let cache = get(AppCache);
    if (cache.lmsCircles) {
      return cache.lmsCircles;
    }
    let response = await makeLmsRequest('/circle/all/meta', 'GET');
    let circles = await response.json();
    circles.forEach(circle => applyLocalizedFields(circle, CIRCLE_LOCALIZED_FIELDS));
    AppCache.update(cache => {
      cache.lmsCircles = circles;
      return cache;
    });
    return circles;
  },
  getCircleDetailsByUrl: async (url) => {
    let response = await makeLmsRequest(`/circle/${url}/details`, 'GET');
    return applyLocalizedCircleDetails(await response.json());
  },
  getCircleTemplate: async (id) => {
    let response = await makeLmsRequest(`/circle/template/${id}/details`, 'GET');
    return applyLocalizedCircleDetails(await response.json());
  },
  getPaidCircleMembers: async (id, query, page, limit) => {
    const params = new URLSearchParams();
    if (query && query.trim().length > 0) {
      params.append('q', query.trim());
    }
    params.append('page', page);
    params.append('limit', limit);

    let response = await makeLmsRequest(`/circle/${id}/members/CIRCLE/paid?${params.toString()}`, 'GET');
    return await response.json();
  },
  getCompletedCircleMembers: async (id, query, page, limit) => {
    const params = new URLSearchParams();
    if (query && query.trim().length > 0) {
      params.append('q', query.trim());
    }
    params.append('page', page);
    params.append('limit', limit);

    let response = await makeLmsRequest(`/circle/${id}/members/CIRCLE/completed?${params.toString()}`, 'GET');
    return await response.json();
  },
  getBannedCircleMembers: async (id, query, page, limit) => {
    const params = new URLSearchParams();
    if (query && query.trim().length > 0) {
      params.append('q', query.trim());
    }
    params.append('page', page);
    params.append('limit', limit);

    let response = await makeLmsRequest(`/circle/${id}/members/CIRCLE/banned?${params.toString()}`, 'GET');
    return await response.json();
  },
  getAccessRequestedMembers: async (id, query, page, limit) => {
    const params = new URLSearchParams();
    if (query && query.trim().length > 0) {
      params.append('q', query.trim());
    }
    params.append('page', page);
    params.append('limit', limit);

    let response = await makeLmsRequest(`/circle/${id}/members/CIRCLE/access_requested?${params}`, 'GET');
    return await response.json();
  },
  getProductIds: (circleDetails) => {
    let productIds = [];
    let circle = circleDetails.circle;
    if (circle.access?.type === 'PAID' && circle.access.productId) {
      productIds.push(circle.access.productId);
    }
    productIds.push(...circleDetails.pages.filter(page => page.access?.type === 'PAID' && page.access.productId).map(page => page.access.productId));
    circleDetails.groups.forEach(group => {
      if (group.pageGroup.access?.type === 'PAID' && group.pageGroup.access.productId) {
        productIds.push(group.pageGroup.access.productId);
      }
      productIds.push(...group.pages.filter(page => page.access?.type === 'PAID' && page.access.productId).map(page => page.access.productId));
    });
    return productIds;
  },
  getProductsById: async (productIds) => {
    let productsResponse = await makeWebsiteRequest({
      action: 'getProductsByIds',
      method: 'post',
      data: {
        productIds: productIds.join(',')
      }
    });

    const productsMap = {};
    productsResponse.data.forEach(product => {
      productsMap[product.id] = {
        productId: product.productId,
        price: product.price,
        subscriptionLbl: product.withSubscription && product.additions.subscription ? External.WebPlatform.Store.subscriptionToStr(product.additions.subscription) : null
      };
    });
    return productsMap;
  },
  cloneProducts: async (productIds) => {
    let result = await makeWebsiteRequest({
      action: 'cloneProducts',
      method: 'post',
      data: {
        products: productIds.join(',')
      }
    });

    if (!result.ok) {
      return null;
    }

    if (result.data === 'NO_STORE_ITEMS_AVAILABLE') {
      External.UITools.showNoMoreProductsDialog(() => {
        External.CPanel.goToUrl('/me/settings/billing');
      });
      return null;
    }
    return result.data;
  },
  getPagesAndGroupsInOrder: (circleDetails) => {
    return [
      ...circleDetails.pages,
      ...circleDetails.groups
    ].sort((first, second) => {
      return (first.pageGroup ? first.pageGroup.uiOrder : first.uiOrder) - (second.pageGroup ? second.pageGroup.uiOrder : second.uiOrder);
    });
  },
  getPagesWithTrackingProgressEnabled: (circleDetails) => {
    // iterate over all circles, pages and groups of pages in them to get the ones which are tracked
    let details = Array.isArray(circleDetails) ? circleDetails : [circleDetails];
    return details.flatMap(circle => [...circle.pages, ...circle.groups.flatMap(group => group.pages)])
    .filter(page => page.settings?.trackContentCompletion);
  },
  getMenuItemNumber: (item, inItems) => {
    if (item.settings?.layout !== 'number') {
      return 0;
    }

    let itemNo = 0;
    for (let idx = 0; idx < inItems.length; ++idx) {
      let checkedItem = inItems[idx];
      if (checkedItem.pageGroup) {
        checkedItem = checkedItem.pageGroup;
      }
      if (checkedItem === item) {
        return ++itemNo;
      }
      if (checkedItem.settings?.layout === 'number') {
        ++itemNo;
      }
    }
    return 0;
  },
  setupMenuSortable: (node, [circle, items, onSorted]) => {

    let toModel = (item) => item.pageGroup ? item.pageGroup : item;

    let jqSortable = External.$(node);
    jqSortable.sortable({
      items: '> .circle-page-item, > .circle-group-wrapper',
      handle: '.move-handle',
      containment: node,
      tolerance: 'pointer',
      axis: 'y',
      start: (event, ui) => {
        ui.item.data('idx', ui.item.index());
      },
      stop: (event, ui) => {
        let newIdx = ui.item.index();
        let oldIdx = ui.item.data('idx') || 0;
        if (newIdx != oldIdx) {
          let item = items[oldIdx];
          items.splice(oldIdx, 1);
          items.splice(newIdx, 0, item);

          let isGroup = !!item.pageGroup;
          item = toModel(item);

          let lowerOrder = newIdx - 1 >= 0 ? toModel(items[newIdx - 1]).uiOrder : 0;
          let higherOrder = newIdx + 1 < items.length ? toModel(items[newIdx + 1]).uiOrder : (lowerOrder + 100);
          item.uiOrder = (lowerOrder + higherOrder) / 2;

          if (isGroup)
            makeLmsRequest(`/circle/${circle.id}/group/${item.id}`, 'PUT', buildLocalizedPayload(item, GROUP_LOCALIZED_FIELDS));
          else
            makeLmsRequest(`/page/${item.id}`, 'PUT', buildLocalizedPayload(item, PAGE_LOCALIZED_FIELDS));
          onSorted();
        }
      }
    });
    return {
      destroy: () => {
        jqSortable.sortable('destroy');
        jqSortable = null;
      },
      update: ([circle, changedItems, onSorted]) => {
        items = changedItems;
      }
    };
  }
};
